import { useState } from "react";
import { ArrowRight, HeartHandshake, UserRound, Users } from "lucide-react";

const OPTIONS = [
  { id: "solo", title: "A mi ritmo", description: "Puedes empezar por tu cuenta y compartir lo que te sirva.", Icon: UserRound },
  { id: "together", title: "En equipo", description: "Si os viene bien, haced el ejercicio del día y la pregunta juntos.", Icon: Users },
];

export default function PackWelcome({ code, onStart }) {
  const startedKey = `alania_welcome_started_${code}`;
  const approachKey = `alania_welcome_approach_${code}`;
  const [started, setStarted] = useState(() => localStorage.getItem(startedKey) === "1");
  const [approach, setApproach] = useState(() => localStorage.getItem(approachKey) || "");

  const start = () => {
    localStorage.setItem(startedKey, "1");
    setStarted(true);
    onStart();
  };

  if (started) {
    return (
      <section data-testid="pack-welcome-collapsed" className="mt-6 flex flex-col gap-3 border-y border-[var(--sand)] py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-2 font-semibold"><HeartHandshake size={18} className="text-[var(--terra)]" /> Un paso pequeño cada día también es hacer equipo.</p>
        <button type="button" onClick={() => setStarted(false)} className="self-start text-sm font-semibold underline underline-offset-4 sm:self-auto">Volver a la bienvenida</button>
      </section>
    );
  }

  return (
    <section data-testid="pack-welcome" className="mt-8 overflow-hidden rounded-lg border border-[var(--sand)] bg-[var(--cream-2)]">
      <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
        <div className="px-5 py-6 sm:px-8 sm:py-8">
          <p className="text-sm font-semibold uppercase text-[var(--sage-deep)]">Tu recorrido · 21 días · unos 10 minutos al día</p>
          <h2 className="mt-2 max-w-3xl font-display text-3xl sm:text-4xl">Criar en equipo no siempre es fácil. No tenéis que resolverlo todo hoy.</h2>
          <p className="mt-4 max-w-3xl text-[#4a4038]">
            Entre las prisas, el cansancio y las diferentes formas de educar, a veces una conversación acaba en discusión. Aquí encontraréis herramientas psicoeducativas para entender qué os pasa, practicar cómo hablarlo y construir acuerdos de crianza posibles, paso a paso.
          </p>
          <p className="mt-3 max-w-3xl text-sm text-[#5a4f46]">
            Cada día propone una práctica breve, una idea para probar y una explicación opcional. Puedes hacerlo por tu cuenta o en pareja; no hace falta estar de acuerdo en todo para empezar.
          </p>
          <details className="mt-4 max-w-3xl rounded-lg border border-[var(--sand)] bg-white/80 p-4">
            <summary className="cursor-pointer font-semibold">¿Qué significa responder con asertividad?</summary>
            <p className="mt-2 text-sm text-[#4a4038]">
              Es expresar con claridad lo que observas, sientes y necesitas, y hacer una petición concreta respetando que la otra persona también puede decir que sí o que no. No es atacar, imponer ni callarte para evitar cualquier desacuerdo; tampoco garantiza que la otra persona cambie.
            </p>
            <div className="mt-4 space-y-3 border-t border-[var(--sand)] pt-4 text-sm">
              <p><strong>Una escena cotidiana:</strong> estás acabando la cena, queda bañar al peque y tu pareja dice que ahora no puede.</p>
              <p className="rounded-lg bg-[var(--cream-2)] p-3"><strong>Petición asertiva:</strong> “Se me está acumulando todo y necesito repartirlo. ¿Puedes ocuparte del baño o recoger la cocina mientras termino?”. Expresa la sobrecarga y propone opciones concretas sin atacar.</p>
              <p className="text-[#5a4f46]"><strong>Reproche:</strong> “Como siempre, me toca todo a mí” puede aumentar la tensión y no concreta qué ayuda necesitas ahora.</p>
              <p className="text-[#5a4f46]"><strong>Callarte:</strong> “No pasa nada” puede evitar el desacuerdo por un momento, pero deja la necesidad sin expresar si se convierte en costumbre.</p>
              <p className="text-xs text-[#7a6c5e]">La otra persona puede seguir diciendo que no; entonces podéis negociar una alternativa o hablar del reparto en otro momento. Si expresarte no es seguro, prioriza tu seguridad y busca apoyo individual.</p>
            </div>
          </details>

          <fieldset className="mt-6">
            <legend className="text-sm font-semibold">¿Cómo te apetece empezar?</legend>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {OPTIONS.map(({ id, title, description, Icon }) => (
                <button key={id} type="button" aria-pressed={approach === id} onClick={() => {
                  localStorage.setItem(approachKey, id);
                  setApproach(id);
                }} className={`flex min-h-24 items-start gap-3 rounded-lg border-2 p-4 text-left transition-colors ${approach === id ? "border-[var(--forest)] bg-white" : "border-[var(--sand)] bg-white/70 hover:bg-white"}`}>
                  <Icon size={20} className="mt-0.5 shrink-0 text-[var(--forest)]" aria-hidden="true" />
                  <span><span className="block font-semibold">{title}</span><span className="mt-1 block text-sm text-[#5a4f46]">{description}</span></span>
                </button>
              ))}
            </div>
          </fieldset>
          <button type="button" onClick={start} className="btn-accent mt-6 w-full sm:w-auto">
            Empezar por el día 1 <ArrowRight size={18} aria-hidden="true" />
          </button>
        </div>
        <figure className="relative min-h-64 bg-[var(--sand)] lg:min-h-full">
          <img src="/equipo-en-calma-portada.png" alt="Ilustración de acuerdos cotidianos para hacer equipo en la crianza" loading="lazy" decoding="async" className="absolute inset-0 size-full object-contain p-5 sm:p-8" />
          <figcaption className="absolute bottom-4 left-4 rounded-lg bg-white/90 px-3 py-2 text-sm font-semibold text-[var(--forest)]">Una práctica a la vez. A vuestro ritmo.</figcaption>
        </figure>
      </div>
    </section>
  );
}
